import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  InputAdornment,
  Autocomplete,
  Typography,
  Box
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useNotification } from '../layouts/Layout';
import subcontractorService, { Subcontractor, UpdateSubcontractorDataWithFile } from '../../services/subcontractorService';
import axios from 'axios';

interface EditSubcontractorFormProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (subcontractor: Subcontractor) => void;
  subcontractor: Subcontractor | null;
  onDeleted?: (id: string) => void;
}

const EditSubcontractorForm: React.FC<EditSubcontractorFormProps> = ({
  open,
  onClose,
  onUpdate,
  subcontractor,
  onDeleted
}) => {
  const [formData, setFormData] = useState<UpdateSubcontractorDataWithFile>({
    contract_number: '',
    name: '',
    email: '',
    phone: '',
    contract_subject: '',
    contract_value: 0,
    completion_date: '',
    signed_date: '',
    progress: 0,
    invoiced_amount: 0
  });
  
  const [contractFile, setContractFile] = useState<File | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { showNotification } = useNotification();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Inicjalizacja formularza danymi podwykonawcy
  useEffect(() => {
    if (open && subcontractor) {
      setFormData({
        contract_number: subcontractor.contract_number,
        name: subcontractor.name,
        email: subcontractor.email || '',
        phone: subcontractor.phone || '',
        contract_subject: subcontractor.contract_subject,
        contract_value: subcontractor.contract_value,
        completion_date: subcontractor.completion_date,
        signed_date: (subcontractor as any).signed_date || '',
        progress: subcontractor.progress,
        invoiced_amount: subcontractor.invoiced_amount
      });
      setContractFile(null);
      setErrors({});
    }
  }, [open, subcontractor]);

  // Obsługa zmiany daty terminu wykonania
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Usunięcie błędu po wprowadzeniu wartości
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Obsługa przesyłania pliku
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setContractFile(file);
      setFormData(prev => ({
        ...prev,
        contract_file: file
      }));
    }
  };

  // Obsługa zmiany pól formularza
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Usunięcie błędu po wprowadzeniu wartości
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Obsługa zmiany pól numerycznych
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const numValue = value === '' ? 0 : parseFloat(value);
    
    if (!isNaN(numValue)) {
      setFormData(prev => ({ ...prev, [name]: numValue }));
      
      // Usunięcie błędu po wprowadzeniu wartości
      if (errors[name]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  // Walidacja formularza
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.completion_date) {
      newErrors.completion_date = 'Termin wykonania jest wymagany';
    }
    
    if (!formData.contract_number || !formData.contract_number.trim()) {
      newErrors.contract_number = 'Numer umowy jest wymagany';
    }
    
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Nazwa podwykonawcy jest wymagana';
    }
    
    if (!formData.contract_subject || !formData.contract_subject.trim()) {
      newErrors.contract_subject = 'Przedmiot umowy jest wymagany';
    }
    
    if (!formData.contract_value || formData.contract_value <= 0) {
      newErrors.contract_value = 'Wartość umowy musi być większa od 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Obsługa aktualizacji podwykonawcy
  const handleSubmit = async () => {
    if (!validateForm() || !subcontractor) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Przygotowanie danych do wysłania
      let dataToSend;
      
      // Jeśli wybrano plik, wysyłamy dane z plikiem
      if (contractFile) {
        dataToSend = { ...formData };
      } else {
        // Jeśli nie wybrano pliku, tworzymy nowy obiekt bez pola contract_file
        const { contract_file, ...dataWithoutFile } = formData;
        dataToSend = dataWithoutFile;
      }
      
      const updatedSubcontractor = await subcontractorService.updateSubcontractor(subcontractor.id, dataToSend);
      showNotification('Podwykonawca został zaktualizowany pomyślnie', 'success');
      onUpdate(updatedSubcontractor);
      onClose();
    } catch (error) {
      console.error('Błąd podczas aktualizacji podwykonawcy:', error);
      showNotification('Wystąpił błąd podczas aktualizacji podwykonawcy', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!subcontractor?.id) return;
    try {
      setLoading(true);
      // Spróbuj soft – jeśli backend zwróci 409, zapytaj o wymuszenie
      try {
        await subcontractorService.deleteSubcontractor(subcontractor.id);
      } catch (e: any) {
        if (e?.response?.status === 409) {
          // wymuś usunięcie razem z protokołami
          await subcontractorService.deleteSubcontractor(`${subcontractor.id}?force=true` as any);
        } else {
          throw e;
        }
      }
      showNotification('Podwykonawca został usunięty', 'success');
      // poinformuj rodzica, aby natychmiast usunął rekord z listy
      try { onDeleted && onDeleted(subcontractor.id); } catch {}
      onClose();
    } catch (error) {
      console.error('Błąd podczas usuwania podwykonawcy:', error);
      showNotification('Nie udało się usunąć podwykonawcy', 'error');
    } finally {
      setLoading(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edytuj podwykonawcę</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              name="contract_number"
              label="Numer umowy"
              value={formData.contract_number}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.contract_number}
              helperText={errors.contract_number}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField name="email" label="Adres e-mail podwykonawcy" value={formData.email} onChange={handleChange} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField name="phone" label="Nr telefonu podwykonawcy" value={formData.phone} onChange={handleChange} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField name="signed_date" label="Data podpisania umowy" type="date" value={formData.signed_date as any} onChange={handleDateChange} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField name="name" label="Nazwa podwykonawcy" value={formData.name} onChange={handleChange} fullWidth required error={!!errors.name} helperText={errors.name} />
          </Grid>
          <Grid item xs={12}>
            <TextField name="contract_subject" label="Przedmiot umowy" value={formData.contract_subject} onChange={handleChange} fullWidth required multiline rows={2} error={!!errors.contract_subject} helperText={errors.contract_subject} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField name="contract_value" label="Wartość umowy" type="number" value={formData.contract_value} onChange={handleNumberChange} fullWidth required InputProps={{ endAdornment: <InputAdornment position="end">PLN</InputAdornment> }} error={!!errors.contract_value} helperText={errors.contract_value} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField name="completion_date" label="Termin wykonania" type="date" value={formData.completion_date} onChange={handleDateChange} fullWidth required InputLabelProps={{ shrink: true }} error={!!errors.completion_date} helperText={errors.completion_date} />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1, textAlign: 'center', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }} onClick={() => document.getElementById('contract-file-input')?.click()}>
              <input id="contract-file-input" type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} style={{ display: 'none' }} />
              <CloudUpload sx={{ fontSize: 40, color: '#004273', mb: 1 }} />
              <Typography variant="body1" gutterBottom>Dodaj plik umowy</Typography>
              <Typography variant="body2" color="textSecondary">{contractFile ? `Wybrany plik: ${contractFile.name}` : 'Kliknij, aby wybrać plik (PDF, DOC, DOCX)'}</Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          <Button color="error" onClick={() => setConfirmingDelete(true)} disabled={!subcontractor}>
            Usuń
          </Button>
        </Box>
        <Button onClick={onClose} color="primary">
          Anuluj
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Zapisz zmiany
        </Button>
      </DialogActions>
      {/* Potwierdzenie usunięcia */}
      <Dialog open={confirmingDelete} onClose={() => setConfirmingDelete(false)}>
        <DialogTitle>Potwierdź usunięcie</DialogTitle>
        <DialogContent>
          <Typography>Czy na pewno chcesz usunąć podwykonawcę "{subcontractor?.name}"?</Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>Tej operacji nie można cofnąć.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingDelete(false)}>Anuluj</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={loading}>
            {loading ? 'Usuwanie...' : 'Usuń'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EditSubcontractorForm;
